import { useQuery } from "@tanstack/react-query";
import { config } from "@/lib/config";

export interface AvailableTenant {
    id: string;
    name: string;
    slug: string;
}

interface FetchAvailableTenantsResponse {
    success: boolean;
    tenants: AvailableTenant[];
    message?: string;
}

// Custom error class for auth-related errors
export class AuthTokenExpiredError extends Error {
    constructor(message: string = "Your session has expired. Please log in again.") {
        super(message);
        this.name = "AuthTokenExpiredError";
    }
}

async function fetchAvailableTenants(temporaryToken: string): Promise<AvailableTenant[]> {
    const response = await fetch(`${config.apiUrl}/api/v1/my/available-tenants`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${temporaryToken}`,
        },
    });

    if (!response.ok) {
        // Handle token expiration specifically
        if (response.status === 401) {
            // Dispatch event to notify auth context of session expiry
            window.dispatchEvent(new CustomEvent("auth:session_expired"));
            throw new AuthTokenExpiredError();
        }
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch available tenants");
    }

    const data: FetchAvailableTenantsResponse = await response.json();
    return data.tenants;
}

export function useFetchAvailableTenants(temporaryToken: string | null) {
    return useQuery({
        queryKey: ["availableTenants", temporaryToken],
        queryFn: () => fetchAvailableTenants(temporaryToken!),
        enabled: !!temporaryToken,
        staleTime: 30000, // 30 seconds
    });
}
